import { AxiosError } from "axios";
import {
  BellRing,
  CalendarDays,
  Clock3,
  Edit3,
  Loader2,
  MapPin,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DayButton as DayPickerDayButton,
  DayPicker,
  UI,
  type DayButtonProps,
  useDayPicker,
} from "react-day-picker";
import { es } from "date-fns/locale";
import { isRequestCancellation } from "@shared/api/client";
import { ConfirmDialog } from "@shared/components/ConfirmDialog";
import {
  createAgendaEvent,
  deleteAgendaEvent,
  getAgenda,
  updateAgendaEvent,
  type AgendaEvent,
  type AgendaEventInput,
  type AgendaEventType,
  type AgendaPayload,
} from "@shared/api/agenda";
import "react-day-picker/dist/style.css";

type AgendaUser = { id: number; nombre: string; correo: string; activo?: boolean };

type Props = {
  scope: "personal" | "admin";
  users?: AgendaUser[];
};

const EMPTY_PAYLOAD: AgendaPayload = { events: [], vacations: [], rest_days: [] };
const TYPE_OPTIONS: Array<{ value: AgendaEventType; label: string }> = [
  { value: "evento", label: "Evento" },
  { value: "reunion", label: "Reunión" },
  { value: "recordatorio", label: "Recordatorio" },
  { value: "otro", label: "Otro" },
];

function ymd(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseYmd(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
}

function monthRange(date: Date) {
  const from = new Date(date.getFullYear(), date.getMonth(), 1);
  from.setDate(from.getDate() - 7);
  const to = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  to.setDate(to.getDate() + 7);
  return { from: ymd(from), to: ymd(to) };
}

function formatDate(value: string) {
  return parseYmd(value).toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function eventTime(event: AgendaEvent) {
  if (event.todo_el_dia) return "Todo el día";
  if (event.hora_inicio && event.hora_fin) return `${event.hora_inicio} - ${event.hora_fin}`;
  return event.hora_inicio ?? "Sin hora";
}

function sortAgendaEvents(events: AgendaEvent[]) {
  return [...events].sort((a, b) => {
    const dateDiff = a.fecha.localeCompare(b.fecha);
    if (dateDiff !== 0) return dateDiff;
    if (a.todo_el_dia !== b.todo_el_dia) return a.todo_el_dia ? -1 : 1;
    const timeDiff = (a.hora_inicio ?? "").localeCompare(b.hora_inicio ?? "");
    return timeDiff !== 0 ? timeDiff : a.id - b.id;
  });
}

function initialForm(date: string, userId?: number): AgendaEventInput {
  return {
    usuario_id: userId,
    titulo: "",
    descripcion: "",
    fecha: date,
    hora_inicio: "09:00",
    hora_fin: "10:00",
    todo_el_dia: false,
    tipo: "evento",
    ubicacion: "",
  };
}

export function AgendaWorkspace({ scope, users = [] }: Props) {
  const admin = scope === "admin";
  const activeUsers = useMemo(() => users.filter((user) => user.activo !== false), [users]);
  const [month, setMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(ymd(new Date()));
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [payload, setPayload] = useState<AgendaPayload>(EMPTY_PAYLOAD);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | AgendaEventType>("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AgendaEvent | null>(null);
  const [form, setForm] = useState<AgendaEventInput>(() => initialForm(selectedDate));
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AgendaEvent | null>(null);
  const cacheRef = useRef(new Map<string, AgendaPayload>());
  const activeRequestRef = useRef<AbortController | null>(null);

  const range = useMemo(() => monthRange(month), [month]);
  const cacheKey = useMemo(
    () => `${scope}:${selectedUserId ?? "all"}:${range.from}:${range.to}`,
    [range.from, range.to, scope, selectedUserId],
  );

  const rememberPayload = useCallback((key: string, value: AgendaPayload) => {
    cacheRef.current.set(key, value);
    setPayload(value);
  }, []);

  const load = useCallback(
    async (showError = false) => {
      const cached = cacheRef.current.get(cacheKey);
      if (cached) {
        setPayload(cached);
        setLoading(false);
        setRefreshing(true);
      } else {
        setLoading(true);
        setRefreshing(false);
      }

      activeRequestRef.current?.abort("agenda-refresh");
      const controller = new AbortController();
      activeRequestRef.current = controller;

      try {
        const next = await getAgenda(
          scope,
          range.from,
          range.to,
          selectedUserId,
          controller.signal,
        );
        if (controller.signal.aborted) return;

        rememberPayload(cacheKey, next);
        setError(null);
      } catch (requestError) {
        if (isRequestCancellation(requestError) || controller.signal.aborted) return;

        if (!cached || showError) {
          setError(
            cached
              ? "No se pudo actualizar la agenda. Se mantienen los últimos datos cargados."
              : "No se pudo cargar la agenda.",
          );
        }
      } finally {
        if (activeRequestRef.current === controller) {
          activeRequestRef.current = null;
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [cacheKey, range.from, range.to, rememberPayload, scope, selectedUserId],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  useEffect(() => {
    return () => activeRequestRef.current?.abort("agenda-unmount");
  }, []);

  const events = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("es");
    return payload.events.filter((event) => {
      const matchesType = typeFilter === "all" || event.tipo === typeFilter;
      const matchesSearch = !query || [event.titulo, event.descripcion, event.ubicacion, event.usuario_nombre]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("es")
        .includes(query);
      return matchesType && matchesSearch;
    });
  }, [payload.events, search, typeFilter]);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, AgendaEvent[]>();
    events.forEach((event) => map.set(event.fecha, [...(map.get(event.fecha) ?? []), event]));
    return map;
  }, [events]);

  const vacationDates = useMemo(() => Array.from(new Set(payload.vacations.map((item) => item.date))).map(parseYmd), [payload.vacations]);
  const restDates = useMemo(() => payload.rest_days.map((item) => parseYmd(item.date)), [payload.rest_days]);
  const eventDates = useMemo(() => Array.from(eventsByDate.keys()).map(parseYmd), [eventsByDate]);
  const selectedEvents = eventsByDate.get(selectedDate) ?? [];
  const selectedVacationNames = Array.from(new Set(payload.vacations.filter((item) => item.date === selectedDate).map((item) => item.usuario_nombre)));
  const selectedRest = payload.rest_days.find((item) => item.date === selectedDate);

  const dayTooltipByDate = useMemo(() => {
    const values = new Map<string, string[]>();
    const add = (date: string, label: string) => {
      const current = values.get(date) ?? [];
      if (!current.includes(label)) current.push(label);
      values.set(date, current);
    };

    payload.rest_days.forEach((restDay) => {
      add(restDay.date, `Descanso de plantilla · ${restDay.name}`);
    });

    payload.vacations.forEach((vacation) => {
      add(vacation.date, `Vacaciones · ${vacation.usuario_nombre}`);
    });

    events.forEach((event) => {
      const type = TYPE_OPTIONS.find((item) => item.value === event.tipo)?.label ?? "Evento";
      add(
        event.fecha,
        `${type} · ${event.titulo}${admin ? ` · ${event.usuario_nombre}` : ""}`,
      );
    });

    return new Map(
      Array.from(values.entries()).map(([date, labels]) => [date, labels.join("\n")]),
    );
  }, [admin, events, payload.rest_days, payload.vacations]);

  const AgendaDayButton = (props: DayButtonProps) => {
    const { classNames } = useDayPicker();
    const date = ymd(props.day.date);
    const tooltip = dayTooltipByDate.get(date);
    const hasEvents = eventsByDate.has(date);
    const hasVacation = payload.vacations.some((item) => item.date === date);
    const hasRest = payload.rest_days.some((item) => item.date === date);
    const activeKinds = [hasEvents, hasVacation, hasRest].filter(Boolean).length;

    const backgroundClass =
      activeKinds > 1
        ? "bg-violet-100 text-violet-900 hover:bg-violet-200"
        : hasRest
          ? "bg-sky-100 text-sky-900 hover:bg-sky-200"
          : hasVacation
            ? "bg-emerald-100 text-emerald-900 hover:bg-emerald-200"
            : hasEvents
              ? "bg-amber-100 text-amber-900 hover:bg-amber-200"
              : "";

    return (
      <DayPickerDayButton
        {...props}
        className={`${classNames[UI.DayButton]} ${backgroundClass}`}
        title={tooltip}
        aria-label={`${formatDate(date)}${tooltip ? `. ${tooltip.replaceAll("\n", ". ")}` : ""}`}
      />
    );
  };

  const today = ymd(new Date());
  const todayCount = events.filter((event) => event.fecha === today).length;
  const upcomingCount = events.filter((event) => event.fecha >= today).length;

  const openCreate = (date = selectedDate) => {
    setEditing(null);
    setForm(initialForm(date, admin ? selectedUserId ?? activeUsers[0]?.id : undefined));
    setFormOpen(true);
  };

  const openEdit = (event: AgendaEvent) => {
    setEditing(event);
    setForm({
      usuario_id: event.usuario_id,
      titulo: event.titulo,
      descripcion: event.descripcion,
      fecha: event.fecha,
      hora_inicio: event.hora_inicio,
      hora_fin: event.hora_fin,
      todo_el_dia: event.todo_el_dia,
      tipo: event.tipo,
      ubicacion: event.ubicacion,
    });
    setFormOpen(true);
  };

  const save = async () => {
    if (!form.titulo.trim() || !form.fecha || (admin && !form.usuario_id)) return;
    setSaving(true);
    setError(null);
    try {
      const saved = editing
        ? await updateAgendaEvent(scope, editing.id, form)
        : await createAgendaEvent(scope, form);

      const nextPayload: AgendaPayload = {
        ...payload,
        events: sortAgendaEvents(
          editing
            ? payload.events.map((event) => (event.id === saved.id ? saved : event))
            : [...payload.events, saved],
        ),
      };
      rememberPayload(cacheKey, nextPayload);
      setFormOpen(false);
      setEditing(null);
      void load(false);
    } catch (requestError) {
      setError(
        requestError instanceof AxiosError
          ? ((requestError.response?.data as { message?: string })?.message ?? "No se pudo guardar el evento.")
          : "No se pudo guardar el evento.",
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (event: AgendaEvent) => {
    try {
      await deleteAgendaEvent(scope, event.id);
      const nextPayload = {
        ...payload,
        events: payload.events.filter((item) => item.id !== event.id),
      };
      rememberPayload(cacheKey, nextPayload);
      setDeleteTarget(null);
      void load(false);
    } catch {
      setError("No se pudo eliminar el evento.");
    }
  };

  return (
    <div className="flex min-h-[640px] flex-col gap-4">
      <section className="rounded-[30px] border border-[#dfe8e4] bg-white/90 p-5 shadow-[0_18px_50px_rgba(15,23,42,.06)]">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-start gap-3">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700"><CalendarDays size={21} /></span>
            <div>
              <div className="text-xs font-black uppercase tracking-[0.16em] text-emerald-700">{admin ? "Agenda de la plantilla" : "Agenda personal"}</div>
              <h3 className="mt-1 text-2xl font-black text-slate-950">Calendario y eventos</h3>
              <p className="mt-1 text-sm font-semibold text-slate-500">{admin ? "Crea y asigna reuniones, recordatorios y eventos a cualquier usuario." : "Organiza tu calendario y consulta junto a tus eventos los días de vacaciones y descanso."}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700"><BellRing size={13} className="mr-1 inline" /> Hoy: {todayCount}</span>
            <span className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-600">Próximos: {upcomingCount}</span>
            <button type="button" onClick={() => void load(true)} className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50" title="Actualizar"><RefreshCw size={16} className={loading || refreshing ? "animate-spin" : ""} /></button>
            <button type="button" onClick={() => openCreate()} className="inline-flex items-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-4 py-2.5 text-sm font-black text-white shadow-[0_18px_40px_rgba(16,185,129,.24)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_24px_52px_rgba(16,185,129,.30)]"><Plus size={15} /> Nuevo evento</button>
          </div>
        </div>
      </section>

      <section className="grid gap-3 rounded-[28px] border border-[#e7ecef] bg-white/90 p-4 lg:grid-cols-[minmax(220px,1fr)_180px_220px]">
        <label className="flex items-center gap-2 rounded-2xl border border-slate-200 px-3 py-2"><Search size={15} className="text-slate-400" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar título, descripción…" className="min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none" /></label>
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as "all" | AgendaEventType)} className="rounded-2xl border border-slate-200 px-3 py-2 text-sm font-semibold"><option value="all">Todos los tipos</option>{TYPE_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
        {admin && <select value={selectedUserId ?? ""} onChange={(event) => setSelectedUserId(event.target.value ? Number(event.target.value) : null)} className="rounded-2xl border border-slate-200 px-3 py-2 text-sm font-semibold"><option value="">Toda la plantilla</option>{activeUsers.map((user) => <option key={user.id} value={user.id}>{user.nombre}</option>)}</select>}
      </section>

      {error && <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">{error}</div>}

      <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,.85fr)]">
        <section className="overflow-hidden rounded-[30px] border border-[#e7ecef] bg-white p-4 shadow-[0_14px_42px_rgba(15,23,42,.05)]">
          <div className={admin ? "admin-vacation-calendar w-full" : "rrhh-agenda-calendar w-full"}>
            <DayPicker
              mode="single"
              selected={parseYmd(selectedDate)}
              onSelect={(date) => date && setSelectedDate(ymd(date))}
              month={month}
              onMonthChange={setMonth}
              locale={es}
              weekStartsOn={1}
              showOutsideDays
              modifiers={{ events: eventDates, vacation: vacationDates, rest: restDates }}
              modifiersClassNames={{
                events: "font-black",
                vacation: "font-black",
                rest: "font-black",
              }}
              components={{ DayButton: AgendaDayButton }}
              className="w-full"
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold"><span className="rounded-full bg-emerald-50 px-3 py-1.5 text-emerald-700">Vacaciones</span><span className="rounded-full bg-sky-100 px-3 py-1.5 text-sky-700">Descanso plantilla</span><span className="rounded-full bg-amber-100 px-3 py-1.5 text-amber-800">Con eventos</span><span className="rounded-full bg-violet-100 px-3 py-1.5 text-violet-800">Varios tipos</span></div>
        </section>

        <section className="min-h-[420px] overflow-hidden rounded-[30px] border border-[#e7ecef] bg-white shadow-[0_14px_42px_rgba(15,23,42,.05)]">
          <div className="border-b border-slate-100 p-4"><div className="text-xs font-black uppercase tracking-[0.14em] text-slate-400">Día seleccionado</div><div className="mt-1 text-lg font-black capitalize text-slate-950">{formatDate(selectedDate)}</div>{selectedRest && <div className="mt-2 rounded-xl bg-sky-50 px-3 py-2 text-xs font-black text-sky-700">Descanso de plantilla · {selectedRest.name}</div>}{selectedVacationNames.length > 0 && <div className="mt-2 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700">Vacaciones · {selectedVacationNames.join(", ")}</div>}</div>
          <div className="max-h-[520px] space-y-3 overflow-auto p-4">
            {loading ? <div className="flex items-center gap-2 text-sm font-semibold text-slate-500"><Loader2 size={16} className="animate-spin" /> Cargando…</div> : selectedEvents.length === 0 ? <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-sm font-semibold text-slate-400">No hay eventos este día.</div> : selectedEvents.map((event) => (
              <article key={event.id} className="rounded-[22px] border border-slate-200 bg-slate-50/55 p-4">
                <div className="flex items-start justify-between gap-3"><div className="min-w-0"><div className="text-xs font-black uppercase tracking-wide text-emerald-700">{TYPE_OPTIONS.find((item) => item.value === event.tipo)?.label ?? event.tipo}</div><h4 className="mt-1 font-black text-slate-950">{event.titulo}</h4>{admin && <div className="mt-1 text-xs font-bold text-slate-500"><Users size={12} className="mr-1 inline" />{event.usuario_nombre}</div>}</div>{event.editable && <div className="flex gap-1"><button type="button" onClick={() => openEdit(event)} className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 bg-white text-slate-500"><Edit3 size={14} /></button><button type="button" onClick={() => setDeleteTarget(event)} className="grid h-8 w-8 place-items-center rounded-lg border border-rose-100 bg-white text-rose-600"><Trash2 size={14} /></button></div>}</div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold text-slate-500"><span><Clock3 size={12} className="mr-1 inline" />{eventTime(event)}</span>{event.ubicacion && <span><MapPin size={12} className="mr-1 inline" />{event.ubicacion}</span>}</div>
                {event.descripcion && <p className="mt-3 whitespace-pre-wrap text-sm font-semibold leading-6 text-slate-600">{event.descripcion}</p>}
                {!event.editable && <div className="mt-3 text-[11px] font-bold text-slate-400">Asignado por {event.creador_nombre ?? "administración"}</div>}
              </article>
            ))}
          </div>
        </section>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/35 p-4 backdrop-blur-sm" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) setFormOpen(false); }}>
          <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-[30px] border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-100 p-5"><div><div className="text-xs font-black uppercase tracking-[0.14em] text-emerald-700">{editing ? "Editar evento" : "Nuevo evento"}</div><h3 className="mt-1 text-xl font-black text-slate-950">{admin ? "Agenda de usuario" : "Mi agenda"}</h3></div><button type="button" onClick={() => setFormOpen(false)} className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-slate-500"><X size={16} /></button></div>
            <div className="grid gap-4 p-5 md:grid-cols-2">
              {admin && <label className="md:col-span-2"><span className="mb-1 block text-xs font-black uppercase text-slate-500">Usuario</span><select required value={form.usuario_id ?? ""} onChange={(event) => setForm((current) => ({ ...current, usuario_id: event.target.value ? Number(event.target.value) : undefined }))} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold"><option value="">Selecciona usuario</option>{activeUsers.map((user) => <option key={user.id} value={user.id}>{user.nombre} · {user.correo}</option>)}</select></label>}
              <label className="md:col-span-2"><span className="mb-1 block text-xs font-black uppercase text-slate-500">Título</span><input value={form.titulo} onChange={(event) => setForm((current) => ({ ...current, titulo: event.target.value }))} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold" /></label>
              <label><span className="mb-1 block text-xs font-black uppercase text-slate-500">Fecha</span><input type="date" value={form.fecha} onChange={(event) => setForm((current) => ({ ...current, fecha: event.target.value }))} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold" /></label>
              <label><span className="mb-1 block text-xs font-black uppercase text-slate-500">Tipo</span><select value={form.tipo} onChange={(event) => setForm((current) => ({ ...current, tipo: event.target.value as AgendaEventType }))} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold">{TYPE_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
              <label className="md:col-span-2 flex items-center gap-2 text-sm font-bold text-slate-600"><input type="checkbox" checked={form.todo_el_dia} onChange={(event) => setForm((current) => ({ ...current, todo_el_dia: event.target.checked }))} /> Todo el día</label>
              {!form.todo_el_dia && <><label><span className="mb-1 block text-xs font-black uppercase text-slate-500">Hora inicio</span><input type="time" value={form.hora_inicio ?? ""} onChange={(event) => setForm((current) => ({ ...current, hora_inicio: event.target.value }))} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold" /></label><label><span className="mb-1 block text-xs font-black uppercase text-slate-500">Hora fin</span><input type="time" value={form.hora_fin ?? ""} onChange={(event) => setForm((current) => ({ ...current, hora_fin: event.target.value }))} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold" /></label></>}
              <label className="md:col-span-2"><span className="mb-1 block text-xs font-black uppercase text-slate-500">Ubicación</span><input value={form.ubicacion ?? ""} onChange={(event) => setForm((current) => ({ ...current, ubicacion: event.target.value }))} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold" placeholder="Sala, videollamada, oficina…" /></label>
              <label className="md:col-span-2"><span className="mb-1 block text-xs font-black uppercase text-slate-500">Descripción</span><textarea value={form.descripcion ?? ""} onChange={(event) => setForm((current) => ({ ...current, descripcion: event.target.value }))} className="min-h-28 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold" /></label>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 p-5"><button type="button" onClick={() => setFormOpen(false)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-black text-slate-600">Cancelar</button><button type="button" onClick={() => void save()} disabled={saving || !form.titulo.trim() || (admin && !form.usuario_id)} className="inline-flex items-center gap-2 rounded-xl bg-[#0b4a2f] px-4 py-2.5 text-sm font-black text-white disabled:opacity-50">{saving && <Loader2 size={14} className="animate-spin" />}{editing ? "Guardar cambios" : "Crear evento"}</button></div>
          </div>
        </div>
      )}

      <ConfirmDialog open={deleteTarget !== null} title="Eliminar evento" message={deleteTarget ? `¿Eliminar «${deleteTarget.titulo}» de la agenda?` : undefined} confirmLabel="Eliminar" destructive onClose={() => setDeleteTarget(null)} onConfirm={() => { if (deleteTarget) void remove(deleteTarget); }} />
    </div>
  );
}
